import type { ConversationAttrValue, ConversationAttrValueObject } from '@/entities/conversations';
import { isConversationAttrLeaf } from '@/entities/conversations/model/utils';
import type {
  AttributeDefinitionsMap,
  SimpleAttribute,
  NestedAttribute,
  NavigationNode,
  ContentRow,
  NodeContent,
  NestedArrayInfo,
  NestedObjectInfo,
} from '../model/types';

/**
 * Проверяет, является ли значение ConversationAttrValue (объект с полем attributeName).
 */
export const isConversationAttrValue = (value: unknown): value is ConversationAttrValue =>
  !!value && typeof value === 'object' && !Array.isArray(value) && 'attributeName' in value;

/**
 * Зарезервированные поля ConversationAttrValue — не являются вложенными атрибутами.
 * Разбор вычисления приходит массивом шагов, поэтому без исключения он был бы
 * принят за вложенный массив атрибутов и лист стал бы раскрываемой строкой.
 */
const RESERVED_ATTR_KEYS = new Set([
  'attributeName',
  'attributeValue',
  'attributeComment',
  'attributeTooltip',
  'attributeExplanation',
  'attributeComputedAt',
]);

/**
 * Проверяет, содержит ли значение атрибута вложенные атрибуты:
 * либо массив, либо другой `ConversationAttrValue`-объект.
 */
export const hasNestedAttributes = (attrValue: ConversationAttrValue): boolean => {
  return Object.entries(attrValue).some(([key, value]) => {
    if (RESERVED_ATTR_KEYS.has(key)) return false;
    if (Array.isArray(value)) return true;
    return isConversationAttrValue(value);
  });
};

/**
 * Возвращает вложенные атрибуты-объекты (не массивы) из `ConversationAttrValue`
 * в виде пар `[ключ, значение]`.
 */
export const getNestedAttributes = (attrValue: ConversationAttrValue): Array<[string, ConversationAttrValue]> => {
  const nestedAttrs: Array<[string, ConversationAttrValue]> = [];
  Object.entries(attrValue).forEach(([key, value]) => {
    if (RESERVED_ATTR_KEYS.has(key)) return;
    if (isConversationAttrValue(value) && typeof value.attributeName === 'string') {
      nestedAttrs.push([key, value]);
    }
  });
  return nestedAttrs;
};

/**
 * Возвращает вложенные атрибуты-массивы из `ConversationAttrValue`
 * в виде пар `[ключ, элементы]`. Пустые массивы не включаются.
 */
export const getArrayAttributes = (attrValue: ConversationAttrValue): Array<[string, ConversationAttrValue[]]> => {
  const arrayAttrs: Array<[string, ConversationAttrValue[]]> = [];
  Object.entries(attrValue).forEach(([key, value]) => {
    if (RESERVED_ATTR_KEYS.has(key)) return;
    if (Array.isArray(value as unknown) && (value as unknown as unknown[]).length > 0) {
      arrayAttrs.push([key, value as unknown as ConversationAttrValue[]]);
    }
  });
  return arrayAttrs;
};

/**
 * Извлекает из элемента массива его отображаемое имя (`attributeName`)
 * и вложенные атрибуты-объекты.
 */
export const getArrayItemEntries = (
  item: ConversationAttrValue,
): { displayName: string | null; nestedAttrs: Array<[string, ConversationAttrValue]> } => {
  const nestedAttrs: Array<[string, ConversationAttrValue]> = [];
  const displayName = typeof item.attributeName === 'string' ? item.attributeName : null;
  Object.entries(item).forEach(([key, value]) => {
    if (isConversationAttrValue(value)) nestedAttrs.push([key, value]);
  });
  return { displayName, nestedAttrs };
};

/**
 * Разделяет значения атрибутов диалога на простые (листовые, без вложений) и вложенные.
 * Простые атрибуты сортируются по имени.
 */
export const separateAttributes = (
  conversationAttrValues: ConversationAttrValueObject,
): { simple: SimpleAttribute[]; nested: NestedAttribute[] } => {
  const simple: SimpleAttribute[] = [];
  const nested: NestedAttribute[] = [];
  Object.entries(conversationAttrValues).forEach(([code, attrValue]) => {
    if (!isConversationAttrValue(attrValue)) return;
    const hasValue = attrValue.attributeValue !== undefined && attrValue.attributeValue !== null;
    const hasNested = hasNestedAttributes(attrValue);
    if (hasValue && !hasNested && isConversationAttrLeaf(attrValue)) {
      simple.push({ code, name: attrValue.attributeName, value: attrValue.attributeValue ?? null });
    } else if (hasNested) {
      nested.push({ code, attrValue });
    } else if (hasValue && isConversationAttrLeaf(attrValue)) {
      simple.push({ code, name: attrValue.attributeName, value: attrValue.attributeValue ?? null });
    }
  });
  simple.sort((a, b) => (a.name < b.name ? -1 : 1));
  return { simple, nested };
};

type BuildHierarchyOptions = {
  parentPath?: string;
  attributeDefinitions?: Map<string, { name?: string }>;
};

/**
 * Результат извлечения контента из одного элемента массива атрибутов.
 */
export type ArrayItemContent = {
  /** Плоские строки с именем и значением атрибута */
  rows: ContentRow[];
  /** Вложенные массивы, найденные внутри элемента */
  nestedArrays: NestedArrayInfo[];
  /** Вложенные объекты, найденные внутри элемента */
  nestedObjects: NestedObjectInfo[];
};

/**
 * Строит плоский список `ContentRow[]` из вложенного объекта `ConversationAttrValue`.
 * Применяется, например, для объектов типа `createdByUser`.
 * Листовые значения добавляются как строки, вложенные объекты и массивы — как строки-заголовки
 * с соответствующими `nestedObjects`/`nestedArrays`.
 */
export const buildContentRowsFromObject = (attrValue: ConversationAttrValue, basePath: string): ContentRow[] => {
  const rows: ContentRow[] = [];
  const nestedArrays: NestedArrayInfo[] = [];
  const nestedObjects: NestedObjectInfo[] = [];

  const hasValue = attrValue.attributeValue !== undefined && attrValue.attributeValue !== null;
  const hasNestedChild = () => getNestedAttributes(attrValue).length > 0 || getArrayAttributes(attrValue).length > 0;

  if (hasValue && !hasNestedChild() && isConversationAttrLeaf(attrValue)) {
    rows.push({
      id: basePath,
      name: attrValue.attributeName ?? '',
      attrValue,
      path: basePath,
    });
    return rows;
  }

  if (hasValue) {
    rows.push({
      id: basePath,
      name: attrValue.attributeName ?? '',
      attrValue,
      path: basePath,
    });
  }

  const { nestedAttrs } = getArrayItemEntries(attrValue);
  for (const [nestedCode, nestedVal] of nestedAttrs) {
    if (!isConversationAttrValue(nestedVal)) continue;
    const nestedPath = `${basePath}.${nestedCode}`;
    const hasNestedVal = nestedVal.attributeValue !== undefined && nestedVal.attributeValue !== null;
    const hasNestedChild = getNestedAttributes(nestedVal).length > 0 || getArrayAttributes(nestedVal).length > 0;

    if (hasNestedVal && !hasNestedChild && isConversationAttrLeaf(nestedVal)) {
      rows.push({
        id: nestedPath,
        name: nestedVal.attributeName ?? nestedCode,
        attrValue: nestedVal,
        path: nestedPath,
      });
    } else if (hasNestedChild) {
      nestedObjects.push({
        key: nestedCode,
        name: typeof nestedVal.attributeName === 'string' ? nestedVal.attributeName : nestedCode,
        attrValue: nestedVal,
      });
    }
  }

  for (const [arrayCode, arrayItems] of getArrayAttributes(attrValue)) {
    const firstItem = arrayItems[0];
    const displayName =
      (typeof firstItem?.attributeName === 'string' ? firstItem.attributeName : null) ??
      (typeof attrValue.attributeName === 'string' ? attrValue.attributeName : arrayCode);
    nestedArrays.push({ key: arrayCode, name: displayName, items: arrayItems });
  }

  for (const obj of nestedObjects) {
    const objPath = `${basePath}.${obj.key}`;
    const placeholder: ConversationAttrValue = {
      attributeName: obj.name,
      attributeValue: null,
      attributeComment: '',
    };
    rows.push({
      id: objPath,
      name: obj.name,
      attrValue: placeholder,
      path: basePath,
      nestedObjects: [obj],
    });
  }
  for (const arr of nestedArrays) {
    const arrPath = `${basePath}.${arr.key}`;
    const placeholder: ConversationAttrValue = {
      attributeName: arr.name,
      attributeValue: null,
      attributeComment: '',
    };
    rows.push({
      id: arrPath,
      name: arr.name,
      attrValue: placeholder,
      path: basePath,
      nestedArrays: [arr],
    });
  }
  return rows;
};

const extractContentFromArrayItem = (
  item: ConversationAttrValue,
  itemPath: string,
  index: number,
): ArrayItemContent => {
  const rows: ContentRow[] = [];
  const nestedArrays: NestedArrayInfo[] = [];
  const nestedObjects: NestedObjectInfo[] = [];

  const hasItemVal = item.attributeValue !== undefined && item.attributeValue !== null;
  if (hasItemVal && isConversationAttrLeaf(item)) {
    rows.push({ id: itemPath, name: item.attributeName ?? String(index), attrValue: item, path: itemPath });
  }

  const { nestedAttrs } = getArrayItemEntries(item);
  for (const [nestedCode, nestedVal] of nestedAttrs) {
    if (!isConversationAttrValue(nestedVal)) continue;
    const nestedPath = `${itemPath}.${nestedCode}`;
    const hasNestedVal = nestedVal.attributeValue !== undefined && nestedVal.attributeValue !== null;
    const hasNestedChild = getNestedAttributes(nestedVal).length > 0 || getArrayAttributes(nestedVal).length > 0;

    if (hasNestedVal && !hasNestedChild && isConversationAttrLeaf(nestedVal)) {
      rows.push({
        id: nestedPath,
        name: nestedVal.attributeName ?? nestedCode,
        attrValue: nestedVal,
        path: nestedPath,
      });
    } else if (hasNestedChild) {
      nestedObjects.push({
        key: nestedCode,
        name: typeof nestedVal.attributeName === 'string' ? nestedVal.attributeName : nestedCode,
        attrValue: nestedVal,
      });
    }
  }

  for (const [arrayCode, arrayItems] of getArrayAttributes(item)) {
    const firstItem = arrayItems[0];
    const displayName =
      (typeof firstItem?.attributeName === 'string' ? firstItem.attributeName : null) ??
      (typeof item.attributeName === 'string' ? item.attributeName : arrayCode);
    nestedArrays.push({ key: arrayCode, name: displayName, items: arrayItems });
  }

  for (const obj of nestedObjects) {
    const objPath = `${itemPath}.${obj.key}`;
    rows.push({
      id: objPath,
      name: obj.name,
      attrValue: { attributeName: obj.name, attributeValue: null, attributeComment: '' },
      path: itemPath,
      nestedObjects: [obj],
    });
  }
  for (const arr of nestedArrays) {
    const arrPath = `${itemPath}.${arr.key}`;
    rows.push({
      id: arrPath,
      name: arr.name,
      attrValue: { attributeName: arr.name, attributeValue: null, attributeComment: '' },
      path: itemPath,
      nestedArrays: [arr],
    });
  }

  return { rows, nestedArrays, nestedObjects };
};

/**
 * Возвращает все листовые `ContentRow` из вложенного массива `NestedArrayInfo`
 * в виде единого плоского списка (без разбивки по элементам).
 */
export const getLeafRowsFromNestedArray = (info: NestedArrayInfo, basePath: string): ContentRow[] =>
  info.items.flatMap((item, i) => extractContentFromArrayItem(item, `${basePath}.${info.key}[${i}]`, i).rows);

/**
 * Возвращает массив таблиц из вложенного массива: каждый элемент массива
 * превращается в отдельный `ContentRow[]` (одна «таблица» на элемент).
 */
export const getTablesFromNestedArray = (info: NestedArrayInfo, basePath: string): ContentRow[][] =>
  info.items.map((item, i) => extractContentFromArrayItem(item, `${basePath}.${info.key}[${i}]`, i).rows);

/**
 * Идентификатор синтетического корневого узла «Общее» в дереве навигации.
 * Создаётся автоматически, если на корневом уровне есть листовые атрибуты.
 */
export const ROOT_GENERAL_NODE_ID = '__general__';

/**
 * Идентификатор верхнеуровневого узла «Основные поля» в иерархии «по группам».
 * Содержит метаданные диалога (`MainFieldsPanel`) и не связан с атрибутами.
 */
export const MAIN_FIELDS_NODE_ID = '__main_fields__';

/** Префикс ID узлов, относящихся к конкретной группе атрибутов. */
export const GROUP_NODE_ID_PREFIX = 'g:';

/** ID верхнеуровневого узла «Без группы» (атрибуты, не входящие ни в одну видимую группу). */
export const NO_GROUP_NODE_ID = 'g:nogroup';

/** Возвращает первый узел в дереве (depth-first). */
export const getFirstNodeId = (nodes: NavigationNode[]): string | null => (nodes.length > 0 ? nodes[0].id : null);

/** Собирает все id узлов в дереве. */
export const getAllNodeIds = (nodes: NavigationNode[]): Set<string> => {
  const ids = new Set<string>();
  const walk = (n: NavigationNode) => {
    ids.add(n.id);
    n.children?.forEach(walk);
  };
  nodes.forEach(walk);
  return ids;
};

/** Собирает id всех узлов-групп (имеющих потомков) в дереве. */
export const getAllGroupNodeIds = (nodes: NavigationNode[]): string[] => {
  const ids: string[] = [];
  const walk = (n: NavigationNode) => {
    if (n.children?.length) {
      ids.push(n.id);
      n.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return ids;
};

/** Возвращает id всех групп-предков узла targetId (путь от корня до родителя узла). */
export const getAncestorGroupIds = (nodes: NavigationNode[], targetId: string): string[] => {
  const search = (list: NavigationNode[], ancestors: string[]): string[] | null => {
    for (const node of list) {
      if (node.id === targetId) return ancestors;
      if (node.children?.length) {
        const found = search(node.children, [...ancestors, node.id]);
        if (found) return found;
      }
    }
    return null;
  };
  return search(nodes, []) ?? [];
};

const buildChildrenFromNestedAttrs = (
  nestedAttrs: Array<[string, ConversationAttrValue]>,
  currentPath: string,
  attributeDefinitions?: Map<string, { name?: string }>,
): NavigationNode[] => {
  const nestedObject: ConversationAttrValueObject = {};
  nestedAttrs.forEach(([k, v]) => {
    nestedObject[k] = v;
  });
  return buildNavigationHierarchy(nestedObject, { parentPath: currentPath, attributeDefinitions });
};

const groupArrayItemsByAttributeName = (
  items: ConversationAttrValue[],
  currentPath: string,
  code: string,
  attributeDefinitions?: Map<string, { name?: string }>,
): Array<{ groupKey: string; name: string; indices: number[] }> => {
  const defName = attributeDefinitions?.get(currentPath)?.name ?? code;
  const byName = new Map<string, number[]>();
  items.forEach((item, i) => {
    const name = (typeof item.attributeName === 'string' ? item.attributeName : null) ?? defName ?? String(i);
    const list = byName.get(name) ?? [];
    list.push(i);
    byName.set(name, list);
  });
  return Array.from(byName.entries()).map(([name, indices]) => ({
    groupKey: `${currentPath}@group:${name}`,
    name,
    indices,
  }));
};

/**
 * Строит дерево узлов навигации (`NavigationNode[]`) из плоского объекта значений атрибутов.
 *
 * - Листовые атрибуты на корневом уровне группируются в синтетический узел `ROOT_GENERAL_NODE_ID`.
 * - Атрибуты с вложениями (объектами/массивами) становятся узлами-родителями с дочерними узлами.
 * - Элементы массивов группируются по `attributeName`, каждая группа — отдельный узел.
 */
export const buildNavigationHierarchy = (
  attributes: ConversationAttrValueObject,
  options: BuildHierarchyOptions = {},
): NavigationNode[] => {
  const { parentPath = '', attributeDefinitions } = options;
  const result: NavigationNode[] = [];
  if (parentPath === '') {
    const allRootRows = extractContentRowsAtLevel(attributes, '');
    const rootLeafRows = allRootRows.filter((r) => !r.path.includes('.'));
    if (rootLeafRows.length > 0) result.push({ id: ROOT_GENERAL_NODE_ID, name: 'Общее' });
  }
  for (const [code, attrValue] of Object.entries(attributes)) {
    if (!attrValue) continue;
    const currentPath = parentPath ? `${parentPath}.${code}` : code;
    if (Array.isArray(attrValue)) {
      const items = attrValue as ConversationAttrValue[];
      const groups = groupArrayItemsByAttributeName(items, currentPath, code, attributeDefinitions);
      for (const { groupKey, name } of groups) result.push({ id: groupKey, name });
      continue;
    }
    if (isConversationAttrValue(attrValue)) {
      const nestedAttrs = getNestedAttributes(attrValue);
      const arrayAttrs = getArrayAttributes(attrValue);
      const name = attrValue.attributeName ?? code;
      const allChildren: NavigationNode[] = [];
      if (nestedAttrs.length > 0) {
        allChildren.push(...buildChildrenFromNestedAttrs(nestedAttrs, currentPath, attributeDefinitions));
      }
      for (const [arrayCode, arrayItems] of arrayAttrs) {
        const arrayPath = `${currentPath}.${arrayCode}`;
        const groups = groupArrayItemsByAttributeName(arrayItems, arrayPath, arrayCode, attributeDefinitions);
        for (const { groupKey, name } of groups) allChildren.push({ id: groupKey, name });
      }
      if (nestedAttrs.length > 0 || arrayAttrs.length > 0)
        result.push({ id: currentPath, name, children: allChildren });
      continue;
    }
    const defName = attributeDefinitions?.get(currentPath)?.name ?? code;
    result.push({
      id: currentPath,
      name: defName,
      children: buildNavigationHierarchy(attrValue as ConversationAttrValueObject, {
        parentPath: currentPath,
        attributeDefinitions,
      }),
    });
  }
  return result;
};

const extractContentRowsAtLevel = (attributes: ConversationAttrValueObject, parentPath: string): ContentRow[] => {
  const rows: ContentRow[] = [];
  for (const [code, attrValue] of Object.entries(attributes)) {
    if (!attrValue || !isConversationAttrValue(attrValue)) continue;
    const currentPath = parentPath ? `${parentPath}.${code}` : code;
    const hasValue = attrValue.attributeValue !== undefined && attrValue.attributeValue !== null;
    const nestedAttrs = getNestedAttributes(attrValue);
    const arrayAttrs = getArrayAttributes(attrValue);
    if (hasValue) {
      rows.push({ id: currentPath, name: attrValue.attributeName ?? code, attrValue, path: currentPath });
    }
    for (const [nestedCode, nestedVal] of nestedAttrs) {
      if (!isConversationAttrValue(nestedVal)) continue;
      const nestedPath = `${currentPath}.${nestedCode}`;
      const hasNestedVal = nestedVal.attributeValue !== undefined && nestedVal.attributeValue !== null;
      const hasNestedChild = getNestedAttributes(nestedVal).length > 0 || getArrayAttributes(nestedVal).length > 0;
      if (hasNestedVal && !hasNestedChild) {
        rows.push({
          id: nestedPath,
          name: nestedVal.attributeName ?? nestedCode,
          attrValue: nestedVal,
          path: nestedPath,
        });
      }
    }
    for (const [arrayCode, arrayItems] of arrayAttrs) {
      const arrayPath = `${currentPath}.${arrayCode}`;
      for (let i = 0; i < arrayItems.length; i++) {
        const item = arrayItems[i];
        if (!isConversationAttrValue(item)) continue;
        const itemPath = `${arrayPath}[${i}]`;
        const { nestedAttrs: itemNested } = getArrayItemEntries(item);
        const hasItemVal = item.attributeValue !== undefined && item.attributeValue !== null;
        if (hasItemVal && itemNested.length === 0) {
          rows.push({ id: itemPath, name: item.attributeName ?? String(i), attrValue: item, path: itemPath });
        }
      }
    }
  }
  rows.sort((a, b) => (a.name < b.name ? -1 : 1));
  return rows;
};

type BuildFlatContentOptions = BuildHierarchyOptions;

/**
 * Строит плоскую карту `nodeId → NodeContent` для всего дерева атрибутов.
 * Каждый `NodeContent` — это массив таблиц (`ContentRow[][]`), соответствующих узлу навигации.
 *
 * - Корневые листовые атрибуты попадают в запись `ROOT_GENERAL_NODE_ID`.
 * - Элементы массивов группируются по `attributeName` (аналогично `buildNavigationHierarchy`).
 * - Используется совместно с `buildNavigationHierarchy`: id узлов в дереве совпадают с ключами карты.
 */
export const buildFlatContentMap = (
  attributes: ConversationAttrValueObject,
  options: BuildFlatContentOptions = {},
): Map<string, NodeContent> => {
  const { parentPath = '' } = options;
  const map = new Map<string, NodeContent>();
  if (parentPath === '') {
    const allRootRows = extractContentRowsAtLevel(attributes, '');
    const rootLeafRows = allRootRows.filter((r) => !r.path.includes('.'));
    if (rootLeafRows.length > 0) map.set(ROOT_GENERAL_NODE_ID, [rootLeafRows]);
  }
  const process = (attrs: ConversationAttrValueObject, basePath: string) => {
    for (const [code, attrValue] of Object.entries(attrs)) {
      if (!attrValue) continue;
      const currentPath = basePath ? `${basePath}.${code}` : code;
      if (Array.isArray(attrValue)) {
        const items = attrValue as ConversationAttrValue[];
        const groups = groupArrayItemsByAttributeName(items, currentPath, code, options.attributeDefinitions);
        for (const { groupKey, indices } of groups) {
          const tables: ContentRow[][] = indices
            .map((i) => extractContentFromArrayItem(items[i], `${currentPath}[${i}]`, i).rows)
            .filter((rows) => rows.length > 0);
          if (tables.length > 0) map.set(groupKey, tables);
        }
        continue;
      }
      if (isConversationAttrValue(attrValue)) {
        const nestedAttrs = getNestedAttributes(attrValue);
        const arrayAttrs = getArrayAttributes(attrValue);
        const directRows = extractContentRowsAtLevel({ [code]: attrValue }, basePath);
        if (directRows.length > 0) map.set(currentPath, [directRows]);
        if (nestedAttrs.length > 0) {
          const nestedObj: ConversationAttrValueObject = {};
          nestedAttrs.forEach(([k, v]) => {
            nestedObj[k] = v;
          });
          process(nestedObj, currentPath);
        }
        for (const [arrayCode, arrayItems] of arrayAttrs) {
          const arrayPath = `${currentPath}.${arrayCode}`;
          const groups = groupArrayItemsByAttributeName(arrayItems, arrayPath, arrayCode, options.attributeDefinitions);
          for (const { groupKey, indices } of groups) {
            const tables: ContentRow[][] = indices
              .map((i) => extractContentFromArrayItem(arrayItems[i], `${arrayPath}[${i}]`, i).rows)
              .filter((rows) => rows.length > 0);
            if (tables.length > 0) map.set(groupKey, tables);
          }
        }
        continue;
      }
      const containerAttrs = attrValue as ConversationAttrValueObject;
      const containerRows = extractContentRowsAtLevel(containerAttrs, currentPath);
      if (containerRows.length > 0) map.set(currentPath, [containerRows]);
      process(containerAttrs, currentPath);
    }
  };
  process(attributes, parentPath);
  return map;
};

/** Префиксирует id всех узлов поддерева заданным префиксом (вида `<prefix>/<originalId>`). */
const prefixNavigationNodeIds = (nodes: NavigationNode[], prefix: string): NavigationNode[] =>
  nodes.map((node) => ({
    ...node,
    id: `${prefix}/${node.id}`,
    children: node.children ? prefixNavigationNodeIds(node.children, prefix) : undefined,
  }));

/** Префиксирует ключи карты контента секций тем же префиксом, что и id узлов. */
const prefixFlatContentMap = (map: Map<string, NodeContent>, prefix: string): Map<string, NodeContent> => {
  const result = new Map<string, NodeContent>();
  for (const [key, value] of map) result.set(`${prefix}/${key}`, value);
  return result;
};

/**
 * Описание группы атрибутов для построения иерархии «по группам».
 *
 * `topLevelPaths` — множество первых сегментов путей атрибутов, входящих в группу.
 * По этим сегментам отбираются ключи в `conversationAttrValues`, относящиеся к группе.
 */
export type GroupedHierarchyInput = {
  code: string;
  name: string;
  topLevelPaths: Set<string>;
};

/**
 * Результат построения иерархии «по группам»: дерево узлов навигации
 * и плоская карта контента секций (см. {@link buildFlatContentMap}).
 */
export type GroupedHierarchyResult = {
  nodes: NavigationNode[];
  flatContentMap: Map<string, NodeContent>;
};

/**
 * Строит иерархию навигации и карту контента, сгруппированные по группам атрибутов.
 *
 * Верхний уровень дерева — узлы групп (в порядке, заданном в `groups`).
 * Если хотя бы один корневой ключ `attrValues` не покрыт ни одной группой,
 * перед группами добавляется узел «Без группы» (`NO_GROUP_NODE_ID`).
 *
 * Внутри каждого узла группы — стандартная иерархия от {@link buildNavigationHierarchy}
 * для значений, отобранных по `topLevelPaths` группы.
 *
 * Идентификаторы узлов и ключи карты контента префиксируются `g:<groupCode>/`
 * (или `g:nogroup/`), чтобы один и тот же путь атрибута мог появиться
 * в разных группах с уникальными id (для якорей и scrollspy).
 */
export const buildGroupedNavigationData = (
  attrValues: ConversationAttrValueObject,
  groups: GroupedHierarchyInput[],
  attributeDefinitions: AttributeDefinitionsMap,
  noGroupLabel: string,
): GroupedHierarchyResult => {
  const nodes: NavigationNode[] = [];
  const flatContentMap = new Map<string, NodeContent>();

  /** Все top-level ключи, покрытые хотя бы одной группой. */
  const coveredTopLevels = new Set<string>();
  for (const group of groups) {
    for (const topLevel of group.topLevelPaths) coveredTopLevels.add(topLevel);
  }

  /**
   * Строит подсекцию (узел верхнего уровня + дочерние) и добавляет её
   * в общий результат с префиксированными id.
   *
   * Если внутри группы — единственный синтетический узел `__general__`
   * без собственных детей, он «схлопывается»: группа становится листом,
   * а её контент берётся напрямую из `__general__`. Это убирает избыточный
   * подзаголовок «Общее» внутри группы вида «Бизнес» → «Общее» → …
   */
  const buildAndAppendSection = (filteredValues: ConversationAttrValueObject, nodeId: string, nodeName: string) => {
    if (Object.keys(filteredValues).length === 0) return;
    const subHierarchy = buildNavigationHierarchy(filteredValues, { attributeDefinitions });
    if (subHierarchy.length === 0) return;
    const subContentMap = buildFlatContentMap(filteredValues, { attributeDefinitions });

    const onlyChild = subHierarchy.length === 1 ? subHierarchy[0] : null;
    const isOnlyGeneralLeaf = onlyChild?.id === ROOT_GENERAL_NODE_ID && (onlyChild.children?.length ?? 0) === 0;

    if (isOnlyGeneralLeaf) {
      const generalContent = subContentMap.get(ROOT_GENERAL_NODE_ID);
      nodes.push({ id: nodeId, name: nodeName });
      if (generalContent) flatContentMap.set(nodeId, generalContent);
      return;
    }

    const prefixedChildren = prefixNavigationNodeIds(subHierarchy, nodeId);
    const prefixedContentMap = prefixFlatContentMap(subContentMap, nodeId);
    nodes.push({ id: nodeId, name: nodeName, children: prefixedChildren });
    for (const [key, value] of prefixedContentMap) flatContentMap.set(key, value);
  };

  // 1. «Без группы» — top-level ключи, не покрытые ни одной группой
  const noGroupValues: ConversationAttrValueObject = {};
  for (const [key, value] of Object.entries(attrValues)) {
    if (!coveredTopLevels.has(key)) noGroupValues[key] = value;
  }
  buildAndAppendSection(noGroupValues, NO_GROUP_NODE_ID, noGroupLabel);

  // 2. Каждая группа в порядке, заданном вызывающим кодом
  for (const group of groups) {
    const groupValues: ConversationAttrValueObject = {};
    for (const [key, value] of Object.entries(attrValues)) {
      if (group.topLevelPaths.has(key)) groupValues[key] = value;
    }
    buildAndAppendSection(groupValues, `${GROUP_NODE_ID_PREFIX}${group.code}`, group.name);
  }

  return { nodes, flatContentMap };
};
